import { FaArtstation } from "react-icons/fa";
import {
  TbBrandInstagram,
  TbBrandGithub,
  TbBrandLinkedin,
  TbBrandLinktree,
} from "react-icons/tb";
import { type MetaFunction } from "react-router";

import { BlockQuote } from "../components/BlockQuote";
import { ProfileVideo } from "../components/ProfileVideo";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { canonical, pageTitle } from "../utils/meta";

const socialLinks = [
  {
    href: "https://www.instagram.com/turtlewave.art",
    label: "Instagram",
    tooltip: "Follow on Instagram",
    icon: <TbBrandInstagram size={26} />,
  },
  {
    href: "https://github.com/JayDosunmu",
    label: "GitHub",
    tooltip: "Follow on GitHub",
    icon: <TbBrandGithub size={24} />,
  },
  {
    href: "https://www.linkedin.com/in/taslimdosunmu/",
    label: "LinkedIn",
    tooltip: "Connect on LinkedIn",
    icon: <TbBrandLinkedin size={24} />,
  },
  {
    href: "https://www.artstation.com/turtlewave",
    label: "ArtStation",
    tooltip: "Follow on ArtStation",
    icon: <FaArtstation size={22} />,
  },
  {
    href: "https://linktr.ee/turtlewave",
    label: "Linktree",
    tooltip: "Latest Linktree",
    icon: <TbBrandLinktree size={24} />,
  },
];

function WhoSection({ inline }: { inline: boolean }) {
  const video = (
    <ProfileVideo
      src="/profile-vid.mp4"
      poster="/profile-vid-poster.png"
      className={
        inline
          ? "mx-auto my-4 w-1/4 min-w-50 rounded"
          : "w-1/4 min-w-50 shrink-0 rounded"
      }
    />
  );

  const content = (
    <div className="about-section-content">
      <p>
        For now, TurtleWave is just me (but that may change in the future 🤔).
      </p>
      {inline && video}
      <p>
        I was born in San Diego, California, to Nigerian and Vietnamese-Scottish
        parents. Many years later, I&apos;ve lived in all four corners of the US
        and travelled to several countries around the world. This background
        infuses into my life philosophy the appreciation and desire for a
        variety of experiences.
      </p>
      <p>
        I like to consider myself an outside-the-box thinker, and love learning
        about and understanding everything I can. The areas I&apos;ve explored
        the deepest are computer science, visual arts, and philosophy.
        <br />
        Oh, and I&apos;m always looking to do awesome stuff 🙌
      </p>
    </div>
  );

  if (inline) return content;

  return (
    <div className="flex flex-row-reverse items-start gap-10">
      {video}
      {content}
    </div>
  );
}

export function About() {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  return (
    <div className="about-page">
      <div className="about-card">
        <BlockQuote className="mx-auto px-8 py-4 font-serif font-semibold italic md:px-12 md:py-6">
          <p className="text-md sm:text-[1.325rem] md:text-[1.65rem]">
            More than anything, this is an expression of three values:
          </p>
          <ul className="list-dash mt-3 space-y-4 text-sm sm:text-lg md:text-xl">
            <li>
              The pursuit of understanding, with an appreciation of our
              ignorance
            </li>
            <li>
              A dedication to refinement, and the many failures required to
              achieve it
            </li>
            <li>And a love for humanity: the acceptance of our flaws</li>
          </ul>
        </BlockQuote>

        <div className="about-body">
          <section className="about-section">
            <h2 className="about-heading mb-4">
              Hey there <span className="not-italic">👋</span>
            </h2>
            <div className="about-section-content">
              <p>
                I&apos;m Taslim, the creator of TurtleWave. I&apos;m super glad
                you&apos;re here!
              </p>
            </div>
          </section>

          <section className="about-section">
            <h2 className="about-heading mb-4">What is TurtleWave?</h2>
            <div className="about-section-content">
              <p>
                TurtleWave is a home for my creative works. I create
                &lt;art/technology/other&gt; to tell compelling stories and to{" "}
                <a
                  href="https://github.blog/developer-skills/career-growth/how-the-github-student-developer-pack-helped-this-student-get-a-tech-job/"
                  className="text-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  share tools
                </a>{" "}
                meant for helping people to pursue more fulfillment in life.
              </p>
              <p>
                On this site, you&apos;ll find my artwork, technology, and
                thoughts that I think are worth sharing.
              </p>
            </div>
          </section>

          <section className="about-section">
            <h2 className="about-heading">Who is TurtleWave?</h2>
            <WhoSection inline={!isDesktop} />
          </section>

          <section className="about-section">
            <h2 className="about-heading">
              Interested in Supporting TurtleWave?
            </h2>
            <div className="about-section-content">
              <p>First off—I&apos;m happy that this resonates with you!</p>
              <p>
                You can support TurtleWave by following on the social channels
                that work best for you (whether via the email newsletter, IG, or
                any of the other social links available). Please also share with
                those you think this would resonate with.
              </p>
              <div className="flex items-center justify-center gap-5 md:justify-start">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target={
                      link.href.startsWith("mailto:") ? undefined : "_blank"
                    }
                    rel={
                      link.href.startsWith("mailto:")
                        ? undefined
                        : "noopener noreferrer"
                    }
                    className="tooltip tooltip-top tooltip-delayed text-nav-dark hover:bg-nav-dark hover:text-nav-light active:bg-nav-light active:text-nav-dark flex size-10 items-center justify-center transition-colors duration-300 ease-out"
                    data-tip={link.tooltip}
                    aria-label={link.label}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
              <p>
                I&apos;m currently looking for a kick-ass cofounder for a
                different venture. Here are the qualities I&apos;m looking for:
              </p>
              <ul className="list-disc space-y-1 pl-5">
                <li>Above everything, a good human</li>
                <li>Not afraid of challenges and failure</li>
                <li>Good at or interested in storytelling</li>
                <li>
                  Passionate about frontend-engineering (or really: last-mile
                  experience for people)
                </li>
                <li>Interested in business</li>
              </ul>
              <p>
                If these qualities make you think of someone specific, and you
                think they&apos;d be interested in connecting, please{" "}
                <a
                  href="mailto:turtlewave.art@gmail.com"
                  className="text-link"
                  aria-label="Email"
                >
                  have them email me
                </a>
                !
              </p>
            </div>
          </section>
          <div className="about-footer">
            <p className="text-[1.3rem] font-semibold">Made with ❤️</p>
            <img
              src="/media/TurtleWave_logo-full_canvas.svg"
              alt="TurtleWave logo"
              className="mt-2 h-11 w-auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export const meta: MetaFunction = () => [
  { title: pageTitle("About") },
  canonical("/about"),
];

export default About;
